/**
 * Shared loader and invalidation for tenant bug-report public config.
 * Used by BugReportCaptureProvider, BugReportButton, and admin save flows.
 */

import { bugReportsApi, type BugReportPublicConfig } from '../../services/api/bugReports'
import type { BugReportTargetSystem } from '../../services/api/bugReportSettings'
import { setBugReportCaptureConfig, type BugReportMode } from './captureConfig'
import { installConsoleCapture } from './consoleCapture'
import { logger } from '../../utils/logger'

type BugReportPublicConfigListener = () => void

const listeners = new Set<BugReportPublicConfigListener>()

export function resolveBugReportMode(cfg: BugReportPublicConfig): BugReportMode {
  return (cfg.mode ?? (cfg.enabled ? 'manual' : 'off')) as BugReportMode
}

export function shouldShowBugReportHeaderButton(cfg: BugReportPublicConfig): boolean {
  const mode = resolveBugReportMode(cfg)
  return mode === 'manual' || mode === 'auto'
}

/**
 * Ticketing system the tenant files into, from the last loaded public config.
 * Kept as module state (rather than threaded through props) so any surface that
 * only needs the label — like the submit modal's hint — can read it synchronously
 * after the config has loaded. Defaults to Azure DevOps before the first load.
 */
let _targetSystem: BugReportTargetSystem = 'azure_devops'

export function getBugReportTargetSystem(): BugReportTargetSystem {
  return _targetSystem
}

/** Display name of the tenant's ticketing system, for user-facing copy. */
export function getBugReportTargetName(): string {
  return _targetSystem === 'jira' ? 'Jira' : 'Azure DevOps'
}

export function applyPublicConfig(cfg: BugReportPublicConfig): void {
  _targetSystem = cfg.target_system ?? 'azure_devops'
  const mode = resolveBugReportMode(cfg)
  const captureConsole = cfg.capture_console ?? mode !== 'off'
  const captureApi = cfg.capture_api_errors ?? mode !== 'off'
  setBugReportCaptureConfig({
    mode,
    captureConsole,
    captureApiErrors: captureApi,
    autoReportFrontend: cfg.auto_report_frontend ?? mode === 'auto',
  })
  if (captureConsole || captureApi) {
    installConsoleCapture()
  }
}

export function resetBugReportPublicConfig(): void {
  setBugReportCaptureConfig({
    mode: 'off',
    captureConsole: false,
    captureApiErrors: false,
    autoReportFrontend: false,
  })
}

let _configPromise: Promise<BugReportPublicConfig> | null = null
let _configTimestamp = 0
const CONFIG_CACHE_MS = 300_000 // 5 min — matches backend TTL

export async function loadBugReportPublicConfig(): Promise<BugReportPublicConfig> {
  const now = Date.now()
  if (_configPromise && now - _configTimestamp < CONFIG_CACHE_MS) {
    const cfg = await _configPromise
    applyPublicConfig(cfg)
    return cfg
  }
  _configTimestamp = now
  _configPromise = bugReportsApi.getConfig().catch((err) => {
    _configPromise = null
    throw err
  })
  const cfg = await _configPromise
  applyPublicConfig(cfg)
  return cfg
}

export function invalidateBugReportConfigCache(): void {
  _configPromise = null
  _configTimestamp = 0
}

export function subscribeBugReportPublicConfig(listener: BugReportPublicConfigListener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function invalidateBugReportPublicConfig(): void {
  for (const listener of listeners) {
    listener()
  }
}

export async function loadBugReportPublicConfigSafe(): Promise<BugReportPublicConfig | null> {
  try {
    return await loadBugReportPublicConfig()
  } catch (e) {
    logger.debug('Bug report public config unavailable', e)
    resetBugReportPublicConfig()
    return null
  }
}
